import { useState, useEffect, lazy, Suspense, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Search, BookOpen, Map, CheckSquare, Clock, Database, Code, AlertCircle, ChevronRight, Star, History, Printer, Download, Link2, BookmarkPlus, BookmarkCheck } from 'lucide-react';

// Lazy load section components for better performance
const OverviewSection = lazy(() => import('./sections/OverviewSection'));
const GettingStartedSection = lazy(() => import('./sections/GettingStartedSection'));
const PortCheckerSection = lazy(() => import('./sections/PortCheckerSection'));
const PortValidatorSection = lazy(() => import('./sections/PortValidatorSection'));
const WorklogsSection = lazy(() => import('./sections/WorklogsSection'));
const Missing7hrReportSection = lazy(() => import('./sections/Missing7hrReportSection'));
const ReportsManagerSection = lazy(() => import('./sections/ReportsManagerSection'));
const DataStructureSection = lazy(() => import('./sections/DataStructureSection'));
const ValidationRulesSection = lazy(() => import('./sections/ValidationRulesSection'));
const TechnicalStackSection = lazy(() => import('./sections/TechnicalStackSection'));
const TroubleshootingSection = lazy(() => import('./sections/TroubleshootingSection'));

export default function KnowledgeBase({ initialSection = 'overview', overlayMode = false }) {
  const [activeSection, setActiveSection] = useState(initialSection);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentSections, setRecentSections] = useState([]);
  const [bookmarkedSections, setBookmarkedSections] = useState([]);
  const [scrollProgress, setScrollProgress] = useState(0);
  const contentRef = useRef(null);

  // Load bookmarks and recent history from localStorage
  useEffect(() => {
    const savedBookmarks = localStorage.getItem('kb-bookmarks');
    const savedRecent = localStorage.getItem('kb-recent');
    
    if (savedBookmarks) {
      try {
        setBookmarkedSections(JSON.parse(savedBookmarks));
      } catch (e) {
        console.error('Failed to load bookmarks:', e);
      }
    }
    
    if (savedRecent) {
      try {
        setRecentSections(JSON.parse(savedRecent));
      } catch (e) {
        console.error('Failed to load recent sections:', e);
      }
    }
  }, []);

  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const scrollTop = contentRef.current.scrollTop;
        const scrollHeight = contentRef.current.scrollHeight - contentRef.current.clientHeight;
        const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
        setScrollProgress(progress);
      }
    };

    const content = contentRef.current;
    if (content) {
      content.addEventListener('scroll', handleScroll);
      return () => content.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Update active section when initialSection prop changes
  useEffect(() => {
    setActiveSection(initialSection);
  }, [initialSection]);

  const sections = [
    {
      id: 'overview',
      title: 'Overview',
      icon: BookOpen,
      category: 'Getting Started',
      Component: OverviewSection
    },
    {
      id: 'getting-started',
      title: 'Installation & Setup',
      icon: Code,
      category: 'Getting Started',
      Component: GettingStartedSection
    },
    {
      id: 'port-checker',
      title: 'Port Checker Guide',
      icon: Map,
      category: 'User Guides',
      Component: PortCheckerSection
    },
    {
      id: 'port-validator',
      title: 'Port Validator Guide',
      icon: CheckSquare,
      category: 'User Guides',
      Component: PortValidatorSection
    },
    {
      id: 'engineering-worklogs',
      title: 'Engineering Worklogs',
      icon: Clock,
      category: 'User Guides',
      Component: WorklogsSection
    },
    {
      id: 'missing-7hr-report',
      title: 'Missing 7hr Report',
      icon: Clock,
      category: 'User Guides',
      Component: Missing7hrReportSection
    },
    {
      id: 'reports-manager',
      title: 'Reports Manager',
      icon: BookOpen,
      category: 'User Guides',
      Component: ReportsManagerSection
    },
    {
      id: 'data-structure',
      title: 'Data Structure Reference',
      icon: Database,
      category: 'Data Reference',
      Component: DataStructureSection
    },
    {
      id: 'validation-rules',
      title: 'Validation Rules',
      icon: CheckSquare,
      category: 'Technical',
      Component: ValidationRulesSection
    },
    {
      id: 'technical-stack',
      title: 'Technical Stack',
      icon: Code,
      category: 'Technical',
      Component: TechnicalStackSection
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting & FAQ',
      icon: AlertCircle,
      category: 'Help',
      Component: TroubleshootingSection
    }
  ];

  const categories = [...new Set(sections.map(s => s.category))];

  const filteredSections = sections.filter(section => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      section.title.toLowerCase().includes(query) ||
      section.category.toLowerCase().includes(query)
    );
  });

  const activeContent = sections.find(s => s.id === activeSection);

  // Navigate to a section and track history
  const navigateToSection = (sectionId) => {
    setActiveSection(sectionId);
    
    // Add to recent history (max 5 items, no duplicates)
    setRecentSections(prev => {
      const filtered = prev.filter(id => id !== sectionId);
      const updated = [sectionId, ...filtered].slice(0, 5);
      localStorage.setItem('kb-recent', JSON.stringify(updated));
      return updated;
    });
    
    // Reset scroll to top
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };

  // Toggle bookmark
  const toggleBookmark = (sectionId) => {
    setBookmarkedSections(prev => {
      const isBookmarked = prev.includes(sectionId);
      const updated = isBookmarked 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId];
      localStorage.setItem('kb-bookmarks', JSON.stringify(updated));
      return updated;
    });
  };

  // Copy section link to clipboard
  const copyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}#kb-${activeSection}`;
    navigator.clipboard.writeText(url).then(() => {
      alert('Link copied to clipboard!');
    });
  };

  // Print current section
  const printSection = () => {
    window.print();
  };

  // Export section as text (simplified)
  const exportSection = () => {
    const sectionTitle = activeContent?.title || 'Knowledge Base';
    const content = contentRef.current?.innerText || '';
    const blob = new Blob([`${sectionTitle}\n\n${content}`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${sectionTitle.replace(/\s+/g, '_')}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`flex h-full ${overlayMode ? 'bg-white' : 'bg-gray-50'} relative`}>
      {/* Scroll Progress Bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 z-50">
        <div 
          className="h-full bg-blue-600 transition-all duration-150"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Sidebar Navigation */}
      <aside className={`${overlayMode ? 'w-64' : 'w-80'} bg-white border-r overflow-y-auto`}>
        <div className="p-6 border-b">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold">Knowledge Base</h2>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Bookmarks Section */}
        {bookmarkedSections.length > 0 && (
          <div className="border-b">
            <div className="p-4 pb-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                <Star className="w-3 h-3" />
                Bookmarks
              </h3>
              <div className="space-y-1">
                {bookmarkedSections.slice(0, 3).map(sectionId => {
                  const section = sections.find(s => s.id === sectionId);
                  if (!section) return null;
                  const Icon = section.icon;
                  return (
                    <Button
                      key={section.id}
                      onClick={() => navigateToSection(section.id)}
                      variant="ghost"
                      size="sm"
                      className={`w-full justify-start gap-2 text-xs ${
                        activeSection === section.id
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      <span className="truncate">{section.title}</span>
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Recent History */}
        {recentSections.length > 0 && (
          <div className="border-b">
            <div className="p-4 pb-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                <History className="w-3 h-3" />
                Recent
              </h3>
              <div className="space-y-1">
                {recentSections.slice(0, 3).map(sectionId => {
                  const section = sections.find(s => s.id === sectionId);
                  if (!section) return null;
                  const Icon = section.icon;
                  return (
                    <Button
                      key={section.id}
                      onClick={() => navigateToSection(section.id)}
                      variant="ghost"
                      size="sm"
                      className={`w-full justify-start gap-2 text-xs ${
                        activeSection === section.id
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      <span className="truncate">{section.title}</span>
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <nav className="p-4">
          {categories.map(category => {
            const categorySections = filteredSections.filter(s => s.category === category);
            if (categorySections.length === 0) return null;

            return (
              <div key={category} className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
                  {category}
                </h3>
                <div className="space-y-1">
                  {categorySections.map(section => {
                    const Icon = section.icon;
                    return (
                      <Button
                        key={section.id}
                        onClick={() => navigateToSection(section.id)}
                        variant="ghost"
                        className={`w-full justify-start gap-2 ${
                          activeSection === section.id
                            ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{section.title}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {searchQuery && filteredSections.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <p>No results found for "{searchQuery}"</p>
          </div>
        )}
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-y-auto" ref={contentRef}>
        {/* Content Header with Actions */}
        {activeContent && (
          <div className="sticky top-0 bg-white border-b shadow-sm z-10">
            <div className={`${overlayMode ? 'px-6 py-3' : 'max-w-4xl mx-auto px-8 py-3'} flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <activeContent.icon className="w-5 h-5 text-blue-600" />
                <div>
                  <h1 className="text-lg font-semibold">{activeContent.title}</h1>
                  <p className="text-xs text-gray-500">{activeContent.category}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleBookmark(activeSection)}
                  title={bookmarkedSections.includes(activeSection) ? 'Remove bookmark' : 'Add bookmark'}
                >
                  {bookmarkedSections.includes(activeSection) ? (
                    <BookmarkCheck className="w-4 h-4 text-yellow-600" />
                  ) : (
                    <BookmarkPlus className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyLink}
                  title="Copy link to this section"
                >
                  <Link2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={exportSection}
                  title="Export as text file"
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={printSection}
                  title="Print this section"
                >
                  <Printer className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className={`${overlayMode ? 'p-6' : 'max-w-4xl mx-auto p-8'}`}>
          {activeContent && (
            <Suspense fallback={
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-4 text-gray-600">Loading content...</p>
                </div>
              </div>
            }>
              <activeContent.Component />
            </Suspense>
          )}
        </div>
      </main>
    </div>
  );
}
